import type { TowerSpec } from '../core/tower';
import type { TowerType } from '../core/types';

/**
 * Plain-English tower descriptions — shared by the in-game "?" tower guide
 * (HudScene) and the ?debug stats tab (DemoScene). No jargon or acronyms:
 * these are read by players, not designers.
 */

/** "every 0.7s" / "every 3s" — the human way to read a fire rate. */
function fireEvery(rate: number): string {
  const period = Math.round((1 / rate) * 10) / 10;
  return `fires every ${period}s`;
}

/**
 * How a melee tower's cleave is described. Every melee tower so far swings a
 * blade, but Boxer Hambi throws a punch — and "slashes" under a boxing glove is
 * the first thing a player reads in his shop row. Callers that pass no type get
 * the blade wording, which is right for the Bladesman and the Ninja.
 */
function meleeVerb(type: TowerType | undefined): string {
  if (type === 'boxer') return 'punches';
  if (type === 'capy') return 'bites';
  if (type === 'dagger') return 'stabs';
  return 'slashes';
}

/**
 * Which number the guide's base-to-max range is showing for this tower. Towers
 * that deal damage show damage; the supports show whatever they DO scale.
 */
export type RangedStat =
  | 'damage'
  | 'slow'
  | 'burn'
  | 'freeze'
  | 'poison'
  | 'heal'
  | 'cash'
  | 'none';

export function rangedStatOf(s: TowerSpec): RangedStat {
  if (s.damage > 0) return 'damage';
  // Horror Hambi: every ball poisons, so the poison IS his damage.
  if (s.poisonArrow && s.poisonArrow.chance >= 1) return 'poison';
  if (s.slow) return 'slow';
  if (s.burn) return 'burn';
  if (s.behavior === 'global-freeze' && s.abilityCooldown) return 'freeze';
  // The Light Shop's supports (spec 2026-10-13).
  if (s.healPulse) return 'heal';
  if (s.killBonus) return 'cash';
  return 'none';
}

/**
 * Special-effect summary for a tower tier, in plain words.
 *
 * `skip` drops the one clause the caller is already showing as a range, so the
 * guide never prints "Slow 40%-85% … slows enemies 40%" — two slow numbers in
 * one row, the second of which looks like a contradiction. The Shop passes
 * nothing and keeps every clause, because its rows carry no range.
 */
export function towerSpecial(s: TowerSpec, type?: TowerType, skip: RangedStat = 'damage'): string {
  const parts: string[] = [];
  if (s.behavior === 'pierce' && s.pierce) parts.push(`pierces up to ${s.pierce} enemies`);
  if (s.aoeRadius) parts.push(`splash damage (${s.aoeRadius}px)`);
  if (s.salvo && s.salvo > 1) parts.push(`${s.salvo}-bomb volley`);
  if (s.slow && skip !== 'slow') parts.push(`slows enemies ${Math.round(s.slow.magnitude * 100)}%`);
  if (s.burn && skip !== 'burn')
    parts.push(
      `${s.damage > 0 ? 'burns' : 'poisons'} ${s.burn.magnitude}/sec for ${s.burn.duration}s`,
    );
  if (s.vulnerable)
    parts.push(
      `enemies take +${Math.round(s.vulnerable.magnitude * 100)}% damage for ${s.vulnerable.duration}s`,
    );
  if (s.behavior === 'global-freeze' && skip !== 'freeze')
    parts.push(`freezes everything ${s.freezeDuration ?? 0}s, every ${s.abilityCooldown ?? 0}s`);
  if (s.behavior === 'melee')
    parts.push(`${meleeVerb(type)} all enemies within ${s.cleaveRadius ?? 0}px`);
  if (s.special)
    parts.push(
      `${type === 'galacticcapy' ? 'black fireball' : 'big bolt'}: ${s.special.damage} dmg / ` +
        `${s.special.interval}s${s.special.neighborSplash ? ', hitting the enemy in front and behind too' : ''}`,
    );
  if (s.neighborSplash) parts.push('also hits the enemy in front and behind');
  // General Capy's runners: a path-shot that rolls an imposter every time.
  if (s.behavior === 'path-shot' && s.pierce)
    parts.push(`a runner up the track, striking up to ${s.pierce} enemies`);
  if (s.spawnRoll && s.spawnRoll.chance > 0)
    parts.push(
      `${Math.round(s.spawnRoll.chance * 100)}% red imposter (${s.spawnRoll.damage} dmg instead)`,
    );
  // Horror Hambi poisons with EVERY ball — not a roll, and not an arrow.
  if (s.poisonArrow && s.poisonArrow.chance >= 1)
    parts.push(
      skip === 'poison'
        ? `poison balls, no hit damage (${s.poisonArrow.duration}s of poison)`
        : `poison balls: ${s.poisonArrow.magnitude}/sec for ${s.poisonArrow.duration}s, no hit damage`,
    );
  // Bow Hambi: tier 1 has no poison band, and "0% poison" must never print.
  else if (s.poisonArrow && s.poisonArrow.chance > 0)
    parts.push(
      `${Math.round(s.poisonArrow.chance * 100)}% poison arrow (no hit damage; ` +
        `${s.poisonArrow.magnitude}/sec for ${s.poisonArrow.duration}s)`,
    );
  if (s.volley && s.volley > 1) {
    // King Capy's gems need an enemy each, so his line says "up to"; Bow
    // Hambi's second arrow doubles up instead, so his does not.
    parts.push(
      s.volleyDistinct
        ? `up to ${s.volley} shots, one each at different enemies`
        : `${s.volley} arrows a shot, at different enemies`,
    );
  }
  if (s.beamShot) parts.push('fires a laser beam');
  if (s.tornado) parts.push(`leaf tornado hits the ${s.tornado.targets} nearest enemies`);
  // Samurai Slicer: the Multi-attacker's second attack, with its own ring.
  if (s.midStrike)
    parts.push(
      `Multi-attacker: ${Math.round(s.midStrike.chance * 100)}% to throw ${s.midStrike.count} ` +
        `daggers (${s.midStrike.damage} each, outer range ${s.midStrike.range})`,
    );
  if (s.splitShot)
    parts.push(
      `${Math.round(s.splitShot.chance * 100)}% chance a shard splits and hits a 2nd enemy`,
    );
  if (s.shardRain)
    parts.push(
      `K–O every ${s.shardRain.interval}s: shard rain, ${s.shardRain.damage} dmg to every enemy`,
    );
  if (s.deathBurst) parts.push(`${s.deathBurst.count} parting shots when destroyed`);
  // The Ham Reaper's second attack (spec 2026-10-12).
  if (s.scytheSweep)
    parts.push(
      `Multi-attacker: every ${s.scytheSweep.interval}s a huge scythe sweeps the screen, ` +
        `${s.scytheSweep.damage} to every enemy`,
    );
  // The Light Shop's supports (spec 2026-10-13).
  if (s.healPulse && skip !== 'heal')
    parts.push(`heals towers in range ${s.healPulse.amount} HP every ${s.healPulse.interval}s`);
  if (s.killBonus && skip !== 'cash')
    parts.push(`+$${s.killBonus.cash} cash for every enemy killed`);
  // The Golden Takeout Bag's three more (spec 2026-09-29).
  if (s.stunImmune && type === 'masher') parts.push('cannot be stunned');
  if (s.allyBoost)
    parts.push(`towers in range deal +${Math.round(s.allyBoost.damage * 100)}% damage`);
  if (s.unique) parts.push('only one at a time');
  if (s.endThem)
    parts.push(
      `END THEM every ${s.endThem.interval}s: ${s.endThem.damage} to every enemy, ` +
        `+${Math.round(s.endThem.boost * 100)}% on all towers and ${s.endThem.minions.count} ` +
        `minions for ${s.endThem.duration}s`,
    );
  if (s.shotRoll) {
    const pc = (n: number) => `${Math.round(n * 100)}%`;
    const { crit, headshot } = s.shotRoll;
    // A band the spec does not declare must never print — "0% headshot" was the
    // thing this guard was written for, and Mini Gunner (no crit band) and King
    // Capy (no headshot band) are each missing the opposite one.
    // The Fire Mage's one band is the blue fireball, and King Capy's is the
    // blue gem, so each is named for what the player actually sees rather than
    // called a "critical".
    const critWord =
      type === 'firemage' ? 'blue fireball' : type === 'kingcapy' ? 'blue gem' : 'critical';
    const bands: string[] = [];
    if (crit) bands.push(`${pc(crit.chance)} ${critWord} (${crit.damage} dmg)`);
    if (headshot) bands.push(`${pc(headshot.chance)} headshot (${headshot.damage} dmg)`);
    if (bands.length > 0) parts.push(bands.join(' · '));
  }
  return parts.join(' · ');
}

/** "40-150", or plain "150" for a number that never improves. */
function span(base: number, max: number, unit = ''): string {
  return base === max ? `${base}${unit}` : `${base}${unit}-${max}${unit}`;
}

/**
 * What the tower hits for, from tier 1 to the best tier it can ever reach.
 *
 * `max` is the TOP tier in the balance table — tier 4 where a tower has an
 * Evolution, whether or not this player has evolved it yet — so the guide reads
 * as a reference for the tower, not a readout of one player's save.
 *
 * The range is deliberately the PLAIN per-hit number: not a crit or headshot
 * roll, not the three blows of the Ninja's flurry, and not a separate special
 * like Phantom Hambi's 1500 bolt (those stay on the special line, where they
 * are explained). One tower's range is then comparable with another's. Timed
 * effects — potions, the Diamond, the Moon Stone — never enter it; they scale
 * whatever this says.
 *
 * Towers that deal no direct damage show the number they DO scale instead:
 * Cheese Guardian its slow, Dark Hambi its burn, Wise Hambi how often it
 * freezes.
 */
function damageSpan(s: TowerSpec, max: TowerSpec): string {
  if (s.damage > 0) return `Damage ${span(s.damage, max.damage)}`;
  if (s.poisonArrow && max.poisonArrow && s.poisonArrow.chance >= 1)
    return `Poison ${span(s.poisonArrow.magnitude, max.poisonArrow.magnitude)}/sec`;
  const pc = (n: number) => Math.round(n * 100);
  if (s.slow && max.slow) return `Slow ${span(pc(s.slow.magnitude), pc(max.slow.magnitude), '%')}`;
  if (s.burn && max.burn) return `Burn ${span(s.burn.magnitude, max.burn.magnitude)}/sec`;
  if (s.behavior === 'global-freeze' && s.abilityCooldown && max.abilityCooldown)
    return `Freezes everything ${s.freezeDuration ?? 0}s, every ${span(s.abilityCooldown, max.abilityCooldown, 's')}`;
  // Healer Hambi's cadence gets SHORTER as he improves, so it reads best-last.
  if (s.healPulse && max.healPulse)
    return `Heals ${s.healPulse.amount} HP every ${span(s.healPulse.interval, max.healPulse.interval, 's')}`;
  if (s.killBonus && max.killBonus)
    return `+$${span(s.killBonus.cash, max.killBonus.cash)} per kill`;
  return 'No direct damage';
}

/**
 * Core stat line for a tower tier: damage · fire cadence · range.
 *
 * Pass `max` (the tower's top tier) to show the damage as a base-to-max range,
 * which the "?" tower guide does. The Shop passes nothing and keeps the single
 * number for the tier it is selling.
 */
export function towerStatLine(s: TowerSpec, max: TowerSpec = s): string {
  const dmg = damageSpan(s, max);
  const rate =
    s.fireRate > 0
      ? fireEvery(s.fireRate)
      : s.behavior === 'melee'
        ? s.strikeWait
          ? `strikes every ${s.strikeWait}s`
          : 'strikes nonstop' // strikeWait 0 — only the dash paces it
        : s.behavior === 'aura-slow' && !s.healPulse && !s.killBonus
          ? 'constant aura'
          : '';
  const rng = s.range > 0 ? `range ${s.range}` : '';
  return [dmg, rate, rng].filter(Boolean).join(' · ');
}

/**
 * Hold a chosen FILL ALL level inside Lv 1…maxTier. Tiers are 1-based, so the
 * player-facing level IS the tier — pass `GameSim.maxTierFor(type)`, which is 4
 * only for a tower that both HAS a tier-4 spec and has been Evolved. Clamping
 * down (rather than refusing the tower) is what makes a Lv 4 choice fall back
 * to Lv 3 the moment a tower with no tier-4 evolution is selected — the stepper
 * would otherwise keep offering a level that silently stamps tier 3.
 */
export function clampFillLevel(level: number, maxTier: 3 | 4): number {
  return Math.max(1, Math.min(maxTier, level));
}
